import Vue from 'vue'
import { uuid } from '@/lib'
import * as action from '../action-types'
import * as mutation from '../mutation-types'

export const state = {
  templates: []
}

export const getters = {
  templates: state => () =>
    state.templates,

  template: state => templateId =>
    state.templates.find(t => t.templateId === templateId),

  overlayData: state => templateId => {
    const template = state.templates.find(t => t.templateId === templateId)
    return template && template.overlay && template.overlay.data ? template.overlay.data : {}
  },

  overlayOptions: state => templateId => {
    const template = state.templates.find(t => t.templateId === templateId)
    return template && template.overlay && template.overlay.options ? template.overlay.options : {}
  },

  templateData: state => templateId => {
    const template = state.templates.find(t => t.templateId === templateId)
    return template && template.data ? template.data : {}
  },

  templateOptions: state => templateId => {
    const template = state.templates.find(t => t.templateId === templateId)
    return template && template.options ? template.options : {}
  },

  folders: state => () => {
    return Object.keys(state.templates.reduce((res, template) => {
      if (template.folder) res[template.folder] = template.folder
      return res
    }, {}))
  },

  nextLayer: state => () => {
    const layer = state.templates.reduce((res, template) => {
      return Math.max(res, template.layer || 0)
    }, -1)
    return layer + 1
  }
}

export const actions = {
  [action.TEMPLATE_CREATE] ({getters, commit, dispatch}, {templateId, componentType, name, overlays, data, options, overlay}) {
    if (!Vue.component(componentType)) {
      componentType = 'template-not-found'
    }
    templateId = templateId || uuid()
    const layer = getters.nextLayer()
    const fileName = overlays[0]
    name = name || 'Unnamed Template'
    const overlayData = overlay && overlay.data ? overlay.data || {} : {}
    const overlayOptions = overlay && overlay.options ? overlay.options || {} : {}
    commit(mutation.TEMPLATE_CREATE, {
      templateId,
      componentType,
      overlayFileName: fileName,
      layer,
      name,
      data: data || {},
      options: options || {},
      overlay: {
        data: overlayData,
        options: overlayOptions
      },
      emit: true
    })
    dispatch(action.WINDOW_CREATE, {
      componentType,
      title: name,
      data: {templateId, contentId: templateId}
    })
    commit(mutation.OVERLAY_ADD, {
      fileName: fileName,
      channelId: 'preview',
      layer,
      data: overlayData,
      options: overlayOptions,
      playing: false,
      emit: true
    })
    commit(mutation.OVERLAY_ADD, {
      fileName: fileName,
      channelId: 'program',
      layer,
      data: overlayData,
      options: overlayOptions,
      playing: false,
      emit: true
    })
  },

  [action.TEMPLATE_OPEN] ({getters, dispatch}, {templateId}) {
    const template = getters.template(templateId)
    if (!template) return
    if (!Vue.component(template.componentType)) {
      template.componentType = 'template-not-found'
    }
    dispatch(action.WINDOW_OPEN_CREATE, {
      componentType: template.componentType,
      title: template.name,
      contentId: templateId
    })
  },

  [action.TEMPLATE_DELETE] ({getters, commit}, {templateId}) {
    const window = getters.windowByContentId(templateId)
    if (window) {
      commit(mutation.WINDOW_DELETE, {
        windowId: window.windowId,
        emit: true
      })
    }
    const template = getters.template(templateId)
    if (template) {
      commit(mutation.OVERLAY_REMOVE, {
        channelId: 'preview',
        layer: template.layer,
        emit: true
      })
      commit(mutation.OVERLAY_REMOVE, {
        channelId: 'program',
        layer: template.layer,
        emit: true
      })
    }
    commit(mutation.TEMPLATE_DELETE, {
      templateId,
      emit: true
    })
  },

  [action.TEMPLATE_LAYER] ({getters, commit}, {templateId, layer}) {
    if (typeof layer !== 'number') throw TypeError('layer must be a number')
    const template = getters.template(templateId)
    if (template) {
      commit(mutation.OVERLAY_REMOVE, {
        channelId: 'preview',
        layer: template.layer,
        emit: true
      })
      commit(mutation.OVERLAY_REMOVE, {
        channelId: 'program',
        layer: template.layer,
        emit: true
      })
    }
    commit(mutation.TEMPLATE_LAYER, {
      templateId,
      layer,
      emit: true
    })
  },

  [action.TEMPLATE_NAME] ({getters, commit}, {templateId, name}) {
    commit(mutation.TEMPLATE_NAME, {
      templateId,
      name,
      emit: true
    })

    const window = getters.windowByContentId(templateId)
    if (!window) return
    commit(mutation.WINDOW_TITLE, {
      windowId: window.windowId,
      title: name,
      emit: true
    })
  },

  [action.TEMPLATE_FOLDER] ({getters, commit}, {templateId, folder}) {
    commit(mutation.TEMPLATE_FOLDER, {
      templateId,
      folder,
      emit: true
    })
  },

  [action.TEMPLATE_DATA] ({commit}, {templateId, data}) {
    commit(mutation.TEMPLATE_DATA, {
      templateId,
      data,
      emit: true
    })
  },

  [action.TEMPLATE_OPTIONS] ({commit}, {templateId, options}) {
    commit(mutation.TEMPLATE_OPTIONS, {
      templateId,
      options,
      emit: true
    })
  },

  [action.TEMPLATE_OVERLAY_DATA] ({commit}, {templateId, data}) {
    commit(mutation.TEMPLATE_OVERLAY_DATA, {
      templateId,
      data,
      emit: true
    })
  },

  [action.TEMPLATE_OVERLAY_OPTIONS] ({commit}, {templateId, options}) {
    commit(mutation.TEMPLATE_OVERLAY_OPTIONS, {
      templateId,
      options,
      emit: true
    })
  }
}

export const mutations = {
  [mutation.TEMPLATE_STATE] (state, templates) {
    templates = (Array.isArray(templates) ? templates : (typeof templates === 'object' ? [templates] : []))
    state.templates = templates
      .map(each => Object.assign({}, {
        overlay: {data: {}, options: {}}
      }, each))
  },

  [mutation.TEMPLATE_CREATE] (state, payload) {
    if (typeof payload.componentType !== 'string') throw TypeError('componentType must be a string')
    if (typeof payload.overlayFileName !== 'string') throw TypeError('overlayFileName must be a string')
    const idx = state.templates.findIndex(t => t.templateId === payload.templateId)
    if (idx >= 0) return
    const template = {
      templateId: payload.templateId,
      name: payload.name || 'Unnamed Template',
      componentType: payload.componentType || null,
      overlayFileName: payload.overlayFileName,
      layer: payload.layer || 0,
      data: payload.data || {},
      options: payload.options || {},
      settings: payload.settings || {},
      overlay: {
        data: (payload.overlay ? payload.overlay.data : {}) || {},
        options: (payload.overlay ? payload.overlay.options : {}) || {}
      }
    }
    state.templates.push(template)
  },

  [mutation.TEMPLATE_DELETE] (state, {templateId}) {
    const idx = state.templates.findIndex(t => t.templateId === templateId)
    if (idx < 0) return
    state.templates.splice(idx, 1)
  },

  [mutation.TEMPLATE_LAYER] (state, {templateId, layer}) {
    if (typeof layer !== 'number') throw TypeError('layer must be a number')
    const template = state.templates.find(t => t.templateId === templateId)
    if (!template) return
    template.layer = layer
  },

  [mutation.TEMPLATE_NAME] (state, {templateId, name}) {
    const template = state.templates.find(t => t.templateId === templateId)
    if (!template) return
    template.name = name
  },

  [mutation.TEMPLATE_FOLDER] (state, {templateId, folder}) {
    const template = state.templates.find(t => t.templateId === templateId)
    if (!template) return
    template.folder = folder
  },

  [mutation.TEMPLATE_DATA] (state, {templateId, data}) {
    const template = state.templates.find(t => t.templateId === templateId)
    if (!template) return
    // Replace entire object for reactive properties to fire
    // Object.assign(template.data, data)
    template.data = Object.assign({}, template.data, data)
  },

  [mutation.TEMPLATE_OPTIONS] (state, {templateId, options}) {
    const template = state.templates.find(t => t.templateId === templateId)
    if (!template) return
    // Replace entire object for reactive properties to fire
    // Object.assign(template.options, options)
    template.options = Object.assign({}, template.options, options)
  },

  [mutation.TEMPLATE_OVERLAY_DATA] (state, {templateId, data}) {
    const template = state.templates.find(t => t.templateId === templateId)
    if (!template) return
    // Replace entire object for reactive properties to fire
    // Object.assign(template.overlay.data, data)
    template.overlay.data = Object.assign({}, template.overlay.data, data)
  },

  [mutation.TEMPLATE_OVERLAY_OPTIONS] (state, {templateId, options}) {
    const template = state.templates.find(t => t.templateId === templateId)
    if (!template) return
    // Replace entire object for reactive properties to fire
    // Object.assign(template.overlay.options, options)
    template.overlay.options = Object.assign({}, template.overlay.options, options)
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/templates.js